import { useCallback, useEffect, useRef } from 'react'
import { ISeriesApi, SeriesMarker, Time } from 'lightweight-charts'

/**
 * Универсальный «снимок» торгового состояния, достаточный для
 * рисования маркеров на графике (общий для Bybit/Binance/Rithmic).
 */
export type TradeStateLike = {
    ts: number // ms unix
    isLong: boolean | null
    positionSize: number
    positionPrice: number | null
    isHedging?: boolean
    hedgingPrice?: number | null
    takeAllInProgress?: boolean
    // Для live-tick chart-обновления (Binance use case).
    bid?: number | null
    ask?: number | null
    timeLastCandle?: number | null // ms unix
    lastCandle?: { o: number; h: number; l: number; c: number } | null
}

/**
 * Отслеживает переходы состояния робота и рисует маркеры:
 *   - первое открытие позиции (LONG/SHORT)
 *   - доливка (stair)
 *   - закрытие основной позиции (TP / take-all / ручное)
 *   - открытие/закрытие хеджа
 *   - срабатывание take-all
 *
 * Live-маркеры (open/stair/close/hedge) копятся из переходов состояния и НЕ
 * персистятся.  `baseMarkers` — постоянный слой (например, маркеры закрытых
 * сделок из БД), который переживает refresh: их передаёт страница, а хук
 * накладывает поверх live-события.  Итог = baseMarkers + live, отсортировано.
 *
 * @param series ссылка на candlestick-series
 * @param state  текущий snapshot из RobotStateUpdate
 * @param timeZoneOffset смещение времени для согласования с TIME_ZONE_OFFSET
 * @param baseMarkers постоянные маркеры (история сделок), не из live-стейта
 */
export const useChartMarkers = (
    series: ISeriesApi<'Candlestick'> | undefined,
    state: TradeStateLike | null,
    timeZoneOffset = 0,
    baseMarkers: SeriesMarker<Time>[] = []
) => {
    const liveRef = useRef<SeriesMarker<Time>[]>([])
    const baseRef = useRef<SeriesMarker<Time>[]>(baseMarkers)
    baseRef.current = baseMarkers
    const prevStateRef = useRef<TradeStateLike | null>(null)

    const applyMarkers = useCallback(() => {
        if (!series) return
        const all = [...baseRef.current, ...liveRef.current]
        all.sort((a, b) => (a.time as number) - (b.time as number))
        // Ограничим до 300 последних — не засорять чарт.
        series.setMarkers(all.length > 300 ? all.slice(-300) : all)
    }, [series])

    // Постоянный слой (история сделок) изменился → перерисовать.
    useEffect(() => {
        applyMarkers()
    }, [applyMarkers, baseMarkers])

    useEffect(() => {
        if (!series || !state) return
        const prev = prevStateRef.current
        const events: SeriesMarker<Time>[] = []

        const t = (Math.floor(state.ts / 1000) + timeZoneOffset) as Time

        const sizeNow = Math.abs(state.positionSize || 0)
        const sizePrev = Math.abs(prev?.positionSize || 0)

        // Открытие/закрытие основной.
        // ВАЖНО: маркер рисуем только на РЕАЛЬНОЙ смене состояния
        // (size 0 → ≠0).  При первом snapshot'е (prev=null) после
        // переподключения фронта мы НЕ знаем когда позиция реально
        // открылась — может быть час назад.  Не врём пользователю,
        // ждём настоящей транзишн-эвенты.
        if (prev && sizePrev === 0 && sizeNow > 0) {
            events.push({
                time: t,
                position: state.isLong ? 'belowBar' : 'aboveBar',
                color: state.isLong ? '#3f8600' : '#cf1322',
                shape: state.isLong ? 'arrowUp' : 'arrowDown',
                text: state.isLong ? 'OPEN LONG' : 'OPEN SHORT',
            })
        } else if (prev && sizePrev > 0 && sizeNow === 0) {
            // закрытие
            events.push({
                time: t,
                position: 'inBar',
                color: '#1677ff',
                shape: 'circle',
                text: state.takeAllInProgress ? 'TAKE-ALL' : 'CLOSE',
            })
        } else if (sizePrev > 0 && sizeNow > sizePrev) {
            // доливка (stair)
            events.push({
                time: t,
                position: state.isLong ? 'belowBar' : 'aboveBar',
                color: '#fa8c16',
                shape: 'square',
                text: `STAIR +${(sizeNow - sizePrev).toFixed(2)}`,
            })
        }

        // Хедж: переход «armed» → «on».
        if (prev && prev.isHedging === false && state.isHedging === true) {
            events.push({
                time: t,
                position: 'aboveBar',
                color: '#9254de',
                shape: 'square',
                text: 'HEDGE OPEN',
            })
        }
        // Хедж: переход «on» → «off».
        if (prev && prev.isHedging === true && state.isHedging === false) {
            events.push({
                time: t,
                position: 'aboveBar',
                color: '#722ed1',
                shape: 'circle',
                text: 'HEDGE CLOSE',
            })
        }

        if (events.length) {
            liveRef.current = [...liveRef.current, ...events]
            applyMarkers()
        }

        prevStateRef.current = state
    }, [series, state, timeZoneOffset, applyMarkers])
}
